import HelperMixin from '../../Shared/HelperMixin.vue';

export default {
    name: 'InventoryReports',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails) {
            this.logout();
            return;
        }
        await this.loadBranches();
        await this.loadAll();
    },

    computed: {
        isMinistryUser() {
            return this.hasInventoryAndStockAccessUi();
        }
    },

    data() {
        return {
            selectedBranch: '',
            branches: [],
            stats: {
                totalProducts: 0,
                nearExpiry: 0,
                lowStock: 0,
                pendingOrders: 0,
                openInventories: 0,
                totalStockValue: 0
            },
            monthlyDispatchData: [],
            orderStatusData: [],
            topDispensedData: [],
            categoryDistData: [],
            expiryItems: [],
            lowStockItems: [],
            chartsRendered: false
        };
    },

    methods: {

        fmt(n) { return (n || 0).toLocaleString('ar'); },
        fmtCurrency(n) {
            if (!n) return '0';
            return Number(n).toLocaleString('ar', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        },
        formatDate(d) {
            if (!d) return '-';
            try { return new Date(d).toLocaleDateString('ar-LY'); } catch { return d; }
        },

        async loadBranches() {
            if (!this.isMinistryUser) return;
            try {
                const resp = await this.$http.GetBranches(1, 500, '');
                this.branches = resp.data.info || resp.data || [];
            } catch { /* ignore */ }
        },

        async loadAll() {
            await Promise.all([
                this.loadDashboard(),
                this.loadExpiryTable(),
                this.loadLowStockTable()
            ]);
        },

        async loadDashboard() {
            try {
                const resp = await this.$http.GetInventoryDashboard(this.selectedBranch || null);
                const d = resp.data || {};
                this.stats = {
                    totalProducts: d.totalProducts || 0,
                    nearExpiry: d.nearExpiry || 0,
                    lowStock: d.lowStock || 0,
                    pendingOrders: d.pendingOrders || 0,
                    openInventories: d.openInventories || 0,
                    totalStockValue: d.totalStockValue || 0
                };
                this.monthlyDispatchData = d.monthlyDispatch || [];
                this.orderStatusData = d.orderStatus || [];
                this.topDispensedData = d.topDispensed || [];
                this.categoryDistData = d.categoryDistribution || [];

                this.$nextTick(() => this.renderCharts());
            } catch (e) {
                console.warn('Dashboard load failed', e);
            }
        },

        async loadExpiryTable() {
            try {
                const resp = await this.$http.GetInventoryExpirySummary(this.selectedBranch || null);
                this.expiryItems = resp.data || [];
            } catch { this.expiryItems = []; }
        },

        async loadLowStockTable() {
            try {
                const resp = await this.$http.GetInventoryReportDetailed(1, 100, this.selectedBranch || null);
                const all = resp.data?.info || [];
                this.lowStockItems = all.filter(r => r.isLowStock);
            } catch { this.lowStockItems = []; }
        },

        renderCharts() {
            if (typeof window.ApexCharts === 'undefined') return;

            this.renderMonthlyDispatchChart();
            this.renderOrderStatusChart();
            this.renderTopDispensedChart();
            this.renderCategoryChart();
        },

        renderMonthlyDispatchChart() {
            const el = document.getElementById('monthlyDispatchChart');
            if (!el) return;
            el.innerHTML = '';
            const months = this.monthlyDispatchData.map(x => x.month || x.label || '');
            const values = this.monthlyDispatchData.map(x => x.quantity || x.value || 0);

            const chart = new window.ApexCharts(el, {
                chart: { type: 'area', height: 280, toolbar: { show: false } },
                series: [{ name: 'الكمية المصروفة', data: values }],
                xaxis: { categories: months },
                colors: ['#7367F0'],
                fill: {
                    type: 'gradient',
                    gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 }
                },
                stroke: { curve: 'smooth', width: 2 },
                dataLabels: { enabled: false },
                grid: { borderColor: 'rgba(115, 103, 240, 0.1)' },
                tooltip: { y: { formatter: v => v.toLocaleString('ar') } }
            });
            chart.render();
        },

        renderOrderStatusChart() {
            const el = document.getElementById('orderStatusChart');
            if (!el) return;
            el.innerHTML = '';
            const labels = ['قيد المراجعة', 'معتمد', 'تم الصرف', 'مرفوض'];
            const values = labels.map((_, i) => {
                const d = this.orderStatusData.find(x => x.status === i + 1);
                return d ? d.count : 0;
            });

            const chart = new window.ApexCharts(el, {
                chart: { type: 'donut', height: 280 },
                series: values,
                labels: labels,
                colors: ['#FF9F43', '#28C76F', '#7367F0', '#EA5455'],
                legend: { position: 'bottom' },
                plotOptions: { pie: { donut: { size: '65%' } } },
                dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` }
            });
            chart.render();
        },

        renderTopDispensedChart() {
            const el = document.getElementById('topDispensedChart');
            if (!el) return;
            el.innerHTML = '';
            const top = this.topDispensedData.slice(0, 10);
            const names = top.map(x => (x.name || '').substring(0, 20));
            const vals = top.map(x => x.quantity || x.value || 0);

            const chart = new window.ApexCharts(el, {
                chart: { type: 'bar', height: 300, toolbar: { show: false } },
                series: [{ name: 'الكمية', data: vals }],
                xaxis: { categories: names },
                colors: ['#00CFE8'],
                plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                dataLabels: { enabled: false },
                grid: { borderColor: 'rgba(0,207,232,0.1)' }
            });
            chart.render();
        },

        renderCategoryChart() {
            const el = document.getElementById('categoryChart');
            if (!el) return;
            el.innerHTML = '';
            const labels = this.categoryDistData.map(x => x.category || x.name || '-');
            const vals = this.categoryDistData.map(x => x.count || x.value || 0);
            if (!vals.length) return;

            const chart = new window.ApexCharts(el, {
                chart: { type: 'pie', height: 300 },
                series: vals,
                labels: labels,
                colors: ['#7367F0', '#28C76F', '#FF9F43', '#00CFE8', '#EA5455', '#9E9E9E'],
                legend: { position: 'bottom' },
                dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` }
            });
            chart.render();
        },

        exportExcel(type) {
            const branchParam = this.selectedBranch ? `&branchId=${this.selectedBranch}` : '';
            let url = '';
            if (type === 'expiry') url = `api/admin/WarehouseReports/ExpiryExport?format=excel${branchParam}`;
            else if (type === 'lowstock') url = `api/admin/WarehouseReports/LowStockExport?format=excel${branchParam}`;
            if (url) window.open(url, '_blank');
        }
    }
};
